import { useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { fetchCartGroupAvailability } from '../../../ducks/cart.duck';
import {
  enumerateDateStrings,
  deriveGroupBlockedDates,
  deriveGroupCheckedDates,
} from '../../../util/cartAvailability';

/**
 * RAS-2509: lazy availability + unified blocked-dates map for a cart-item group's
 * date picker.
 *
 * `fetchAvailability` must only be called from a user interaction handler (the picker
 * opening, or navigating the calendar to a new month) - never on mount - per the
 * ticket's non-negotiable performance requirement. Parallel fetch, per-listingId
 * caching and the fail-safe (a failed listing blocks its whole requested range) are
 * handled by the fetchCartGroupAvailability thunk itself; this hook's job is exposing
 * that progressively - `blockedDates` grows as each listing's fetch settles, instead
 * of the caller waiting for the whole group.
 *
 * `checkedDates` (RAS-2506 follow-up) tracks every date that has actually been
 * verified by a settled fetch, separate from `blockedDates` - a date can be "known
 * available" (checked, not blocked) or "unknown" (never requested yet). Callers
 * fetch narrow ranges (the currently visible month plus a small buffer, not the whole
 * booking window) and grow this incrementally as the user navigates the calendar, so
 * this distinction is what lets the calendar treat an unchecked day as blocked-until-
 * proven-otherwise instead of silently treating "never checked" the same as
 * "confirmed available" - the same safe default the listing page's own booking
 * calendar (BookingDatesForm.js's isDayBlockedFn) already applies to a month it
 * hasn't fetched yet.
 *
 * RAS-2506 follow-up (stale-group-membership fix): takes the group's current
 * `listingIds` as an argument. State is tracked PER LISTING internally
 * (blockedDatesByListing / checkedDatesByListing) and the group-level Sets this hook
 * returns are DERIVED from only the listings currently passed in
 * (deriveGroupBlockedDates / deriveGroupCheckedDates, util/cartAvailability.js) - a
 * listing that leaves the group (item removed from the cart) stops contributing to
 * the group view on the very next render, with no explicit invalidation needed. The
 * old version merged every listing's data into one flat, monotonically-growing Set
 * with no way to un-contribute a listing once added, which is exactly what let a
 * removed-but-unavailable item's blocked dates keep blocking the remaining, actually
 * available item(s) in the group.
 */
export const useCartGroupAvailability = (listingIds = []) => {
  const dispatch = useDispatch();
  const [blockedDatesByListing, setBlockedDatesByListing] = useState(() => ({}));
  const [checkedDatesByListing, setCheckedDatesByListing] = useState(() => ({}));
  const [loadingListingIds, setLoadingListingIds] = useState(() => new Set());
  const [failedListingIds, setFailedListingIds] = useState(() => new Set());

  const fetchAvailability = useCallback(
    (listingIdsToFetch, rangeStart, rangeEnd, timeZone) => {
      const uniqueListingIds = [...new Set((listingIdsToFetch || []).filter(Boolean))];
      if (!uniqueListingIds.length || !rangeStart || !rangeEnd || !timeZone) {
        return Promise.resolve({ blockedDates: new Set(), failedListingIds: [] });
      }

      // Merge (not replace) - the calendar can trigger a fresh call (month
      // navigation) while a previous one is still settling.
      setLoadingListingIds(prev => new Set([...prev, ...uniqueListingIds]));

      const onListingSettled = ({ listingId, blockedDates: listingBlockedDates, failed }) => {
        setBlockedDatesByListing(prev => {
          const next = new Set(prev[listingId] || []);
          listingBlockedDates.forEach(d => next.add(d));
          return { ...prev, [listingId]: next };
        });
        setCheckedDatesByListing(prev => {
          const next = new Set(prev[listingId] || []);
          enumerateDateStrings(rangeStart, rangeEnd, timeZone).forEach(d => next.add(d));
          return { ...prev, [listingId]: next };
        });
        setLoadingListingIds(prev => {
          const next = new Set(prev);
          next.delete(listingId);
          return next;
        });
        setFailedListingIds(prev => {
          const next = new Set(prev);
          if (failed) {
            next.add(listingId);
          } else {
            next.delete(listingId);
          }
          return next;
        });
      };

      return dispatch(
        fetchCartGroupAvailability(uniqueListingIds, rangeStart, rangeEnd, timeZone, onListingSettled)
      );
    },
    [dispatch]
  );

  // Cheap, hand-rolled key (not JSON.stringify) - same pattern
  // ListingPageCarousel.js's cartDrawerItemsKey already uses, for the same reason:
  // listingIds is a fresh array reference every render even when its contents
  // haven't changed, and these derivations should only recompute when the actual
  // listing membership (or the underlying fetched data) changes.
  const listingIdsKey = listingIds.join('|');

  const blockedDates = useMemo(
    () => deriveGroupBlockedDates(listingIds, blockedDatesByListing),
    [listingIdsKey, blockedDatesByListing]
  );
  const checkedDates = useMemo(
    () => deriveGroupCheckedDates(listingIds, checkedDatesByListing),
    [listingIdsKey, checkedDatesByListing]
  );
  const currentFailedListingIds = useMemo(
    () => new Set(listingIds.filter(id => failedListingIds.has(id))),
    [listingIdsKey, failedListingIds]
  );
  const isLoading = listingIds.some(id => loadingListingIds.has(id));

  return {
    blockedDates,
    checkedDates,
    failedListingIds: currentFailedListingIds,
    isLoading,
    fetchAvailability,
  };
};
